import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, Lock, Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import AuthShell from "../../components/AuthShell";
import GoogleButton from "../../components/GoogleButton";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail, DELIVERY_ZONES } from "../../lib/constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    delivery_zone: "Dorchester",
  });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const { name, email, phone, password } = form;
    if (!name || !email || !phone || !password) {
      setError("All fields are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      navigate("/home");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to register.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full h-[52px] pl-11 pr-4 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors";

  return (
    <AuthShell title="Create account" subtitle="Join Ayome and shop the next Market Day.">
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div
            data-testid="register-error"
            className="flex items-start gap-2 rounded-xl bg-[#FFF0F0] border border-[#FFD0D0] px-4 py-3 text-sm text-[#991B1B]"
          >
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <div className="relative">
          <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="register-name-input" placeholder="Full name" value={form.name} onChange={set("name")} className={field} />
        </div>
        <div className="relative">
          <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="register-email-input" placeholder="Email" value={form.email} onChange={set("email")} className={field} />
        </div>
        <div className="relative">
          <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="register-phone-input" placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} />
        </div>
        <div className="relative">
          <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="register-password-input"
            type={show ? "text" : "password"}
            placeholder="Password (min 6 chars)"
            value={form.password}
            onChange={set("password")}
            className={field + " pr-11"}
          />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <div>
          <label className="text-sm text-gray-500 mb-1.5 block">Delivery zone</label>
          <Select value={form.delivery_zone} onValueChange={(v) => setForm({ ...form, delivery_zone: v })}>
            <SelectTrigger data-testid="register-zone-select" className="h-[52px] rounded-xl bg-gray-100 border-transparent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_ZONES.map((z) => (
                <SelectItem key={z.name} value={z.name}>
                  {z.name} {z.charge > 0 ? `(+£${z.charge})` : "(Free)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          data-testid="register-submit-button"
          type="submit"
          disabled={loading}
          className="w-full h-[52px] rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold transition-colors duration-200 flex items-center justify-center disabled:opacity-60"
        >
          {loading ? <Loader2 size={20} className="animate-spin" /> : "Create Account"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase tracking-wide">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
      <GoogleButton label="Sign up with Google" />
      <p className="mt-6 text-center text-sm text-gray-500">
        Already have an account?{" "}
        <Link data-testid="go-login-link" to="/login" className="text-[#228B22] font-semibold hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
